import {
  NO_VALUE_LABEL,
  PIPELINE_COLUMNS,
  STAGE_LABEL,
  addCents,
  bidTypeLabel,
  cents,
  followUpStatus,
  formatMoney,
  hasValue,
  isReplacement,
  isTestJob,
  jobAddress,
  jobMatches,
  listSummary,
  onRepPipeline,
  pipelineColumn,
  withinDateFilter,
  type Cents,
  type DateFilter,
  type Job,
  type Profile,
} from "@roof-hawk/core";
import { useCallback, useMemo, useState } from "react";

import { ErrorNote, JobsSummary, ListHead } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchUnknownNumbers } from "../../lib/queries";
import type { AppData } from "../shell/Shell";
import { NewLeadBucket } from "./NewLeadBucket";

/**
 * The four-column pipeline — the screen a rep lands on at sign-in.
 *
 * A signed job sits under Signed — Needs SENDS until the rep presses Sends,
 * and only then moves on to Ongoing Jobs (ADR 0300). Which column a job is in
 * is `pipelineColumn` in packages/core.
 *
 * Three things are colour-coded, and each owns exactly one part of the card so
 * they cannot be confused with one another:
 *
 *   the column header  where the job is in its life
 *   the card's edge    repair or replacement
 *   the top bar        the follow-up clock (packages/core/src/followup.ts)
 *
 * The clock used to be a small tag in the card's footer, the same size and
 * shape as the bid-type tag beside it — the one place in the app where two
 * things meaning completely different amounts looked alike. A job nobody has
 * called in two days now takes the whole top of its card, and the card tints
 * under it. Everything that is not shouting stays quiet, which is what makes
 * the shouting work.
 *
 * Lost and closed jobs are not here by design; they stay on the Master list and
 * the customer's history. Nor is a finished job its rep has sent to Kelsey to
 * close: from then on it is on her board (ADR 0273).
 */
export function Pipeline({
  person,
  data,
  search,
}: {
  person: Profile;
  data: AppData;
  search: string;
}) {
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");

  /**
   * Everybody who has called or texted and is in nobody's records.
   *
   * Fetched here rather than in the bucket so that the New Leads column can
   * count them: a column header that says 3 while four cards sit under it is
   * the kind of small lie that makes people stop trusting the number.
   *
   * Not everybody's board carries it — `takes_new_leads` is Jonathon, Lysa and
   * Samuel — but the bucket itself is shared, so claiming one empties it for
   * all three.
   */
  const unclaimed = useAsync(
    () =>
      person.takes_new_leads ? fetchUnknownNumbers() : Promise.resolve([]),
    [person.id, person.takes_new_leads],
  );
  const strangers = unclaimed.data ?? [];

  const claimed = useCallback(async () => {
    await Promise.all([unclaimed.reload(), data.reload()]);
  }, [unclaimed, data]);

  const rows = data.jobs
    .filter((job) => job.rep_id === person.id || job.alt_rep_id === person.id)
    // Off the board once closed, lost, or sent to Kelsey to close. Jonathon,
    // 2026-10-06: "once it's marked send to kelsey it's removed from the reps
    // dashboard" (ADR 0273).
    .filter((job) => onRepPipeline(job, Boolean(data.closeouts?.has(job.id))))
    .filter((job) => withinDateFilter(job, dateFilter))
    .filter((job) => jobMatches(job, search));

  const values = data.values;
  const approvals = data.approvals;
  const summary = useMemo(
    () => (values ? listSummary(rows, values, new Date(), approvals) : null),
    // `rows` is rebuilt every render; the ids and the filters are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows.map((job) => job.id).join(","), values, dateFilter, approvals],
  );

  return (
    <>
      {/* The "needs a call" chip that used to sit up here has gone: the strip
          below carries the same count, in red, with the reason under it. Two
          copies of one number is not twice the warning. */}
      <ListHead
        title={t("{name} — Pipeline", { name: String(person.full_name) })}
        count={rows.length}
        dateFilter={dateFilter}
        onDateFilter={setDateFilter}
      />
      <JobsSummary summary={summary} />
      <ErrorNote error={unclaimed.error ?? null} />
      <div className="pipe four">
        {PIPELINE_COLUMNS.map((column, index) => {
          const columnJobs = rows.filter(
            (job) => pipelineColumn(job) === column.key,
          );
          // The unclaimed bucket belongs at the head of the first column, which
          // is where somebody looks for work that has just arrived.
          const bucket = index === 0 ? strangers : [];
          return (
            <div className={`pcol tone-${column.tone}`} key={column.key}>
              <h3>
                <span>{t(column.title)}</span>
                <span className="c">{columnJobs.length + bucket.length}</span>
                <span className="ctot">{columnTotal(columnJobs, values)}</span>
              </h3>
              {bucket.length > 0 && (
                <NewLeadBucket rows={bucket} onClaimed={claimed} />
              )}
              {columnJobs.length === 0 && bucket.length === 0 ? (
                <div className="none">{t("None")}</div>
              ) : (
                columnJobs.map((job) => (
                  <PipelineCard
                    key={job.id}
                    job={job}
                    value={values?.get(job.id)}
                    loaded={Boolean(values)}
                  />
                ))
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * What the column is holding.
 *
 * Practice jobs are counted as cards and left out of the money, the same line
 * the sold board draws. A blank while the figures are still coming, because a
 * total that is about to change is worse than none.
 */
function columnTotal(
  jobs: readonly Job[],
  values: ReadonlyMap<string, Cents> | null,
): string {
  if (!values) return "";
  const total = addCents(
    ...jobs
      .filter((job) => !isTestJob(job))
      .map((job) => values.get(job.id) ?? cents(0)),
  );
  return formatMoney(total);
}

function PipelineCard({
  job,
  value,
  loaded,
}: {
  job: Job;
  value: Cents | undefined;
  loaded: boolean;
}) {
  const status = followUpStatus(job);
  const kind = isReplacement(job.bid_type) ? "replacement" : "repair";
  const needsCall = status.level === "alert";

  return (
    /* A card is a real link, opened in its own tab. A rep works the board by
       going into a job and coming back out again; opening the job over the top
       of the board lost their place in it every time, and the browser's Back
       was the only way back. Jonathon, 2026-09-23. It being an `a` rather than
       a div is also what makes the whole card middle-clickable, copyable and
       keyboard-reachable without any of it being written here. */
    <a
      className={`pcard ${kind}${needsCall ? " needs-call" : ""}`}
      href={`#job=${job.id}`}
      target="_blank"
      rel="noreferrer"
    >
      {needsCall && (
        <div className="callbar">
          <span>{t("Call today")}</span>
          <span className="since">
            {status.everContacted ? status.label : t("Never called")}
          </span>
        </div>
      )}
      <div className={needsCall ? "pcard-body" : undefined}>
        <div className="pcard-top">
          <div className="cn">{job.contact?.full_name ?? "—"}</div>
          {loaded && (
            <div className={`pval${hasValue(value) ? "" : " none"}`}>
              {hasValue(value)
                ? formatMoney(value as Cents)
                : t(NO_VALUE_LABEL)}
            </div>
          )}
        </div>
        <div className="ca">{jobAddress(job)}</div>
        <div className="pcard-foot">
          <span className={`pill ${job.bid_type}`}>
            {t(bidTypeLabel(job.bid_type))}
          </span>
          {/* The clock's own tag stays off a card that is already shouting it
              from the top — the bar and the tag would be the same fact twice. */}
          {!needsCall && (
            <span className={`cbadge ${status.level}`}>{status.label}</span>
          )}
        </div>
        <div className="pstage">{t(STAGE_LABEL[job.stage])}</div>
      </div>
    </a>
  );
}
